import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Divider,
  IconButton,
  alpha,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  ContactPhone as ContactIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { isPublicFreePlan } from '../../utils/storePlan';
import ProductService from '../../services/productService';
import StoreService from '../../services/storeService';
import ServiceComboService, { mergeCatalogCombos } from '../../services/serviceComboService';
import { isCustomerBookableService } from '../../utils/freeServiceUtils';
import { useSupportChat } from '../../context/SupportChatContext';
import {
  formatServiceDurationLabel,
  formatServicePriceLabel,
  hasCustomerBookingDuration,
} from '../../utils/serviceValueFormat';
import api from '../../services/api';
import { useStore } from '../../context/StoreContext';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { formatCurrency, getCurrencySymbol } from '../../utils/currencyUtils';
import { DEFAULT_LANGUAGE, getLocalizedValue, normalizeLanguage } from '../../constants/Languages';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

const StoreDetailPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { storeId, storeOverview: contextOverview, loading: contextOverviewLoading } = useStore();
  const { openChat } = useSupportChat();
  const canClickServiceRows = useMediaQuery(FINE_POINTER_QUERY, { noSsr: true });
  const currentLanguage = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  
  const [overview, setOverview] = useState(null);
  const [services, setServices] = useState(null);
  const [products, setProducts] = useState(null);
  const [teamMembers, setTeamMembers] = useState(null);
  const [about, setAbout] = useState(null);
  
  const [loadingStates, setLoadingStates] = useState({
    overview: false,
    location: false,
    images: false,
    services: false,
    products: false,
    team: false,
    about: false,
  });
  
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadedSections, setLoadedSections] = useState(new Set());
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullScreenViewerOpen, setFullScreenViewerOpen] = useState(false);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);
  const [openingHoursExpanded, setOpeningHoursExpanded] = useState(false);
  const [showTabs, setShowTabs] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  
  const sectionsRef = useRef({});
  const loadingSectionsRef = useRef(new Set());
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchStartFullScreenRef = useRef({ x: 0, y: 0, time: 0 });
  const carouselSwipeDetectedRef = useRef(false);
  const fullScreenSwipeDetectedRef = useRef(false);

  // Swipe handler for main carousel
  const handleCarouselTouchStart = (e) => {
    carouselSwipeDetectedRef.current = false;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  };

  const handleCarouselTouchEnd = (e) => {
    if (!overview?.images || overview.images.length <= 1) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    const deltaX = touchStartRef.current.x - touchEnd.x;
    const deltaY = touchStartRef.current.y - touchEnd.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    const minSwipeDistance = 50;
    const maxSwipeTime = 300; // milliseconds

    // Only handle horizontal swipes (ignore if vertical swipe is larger)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance && deltaTime < maxSwipeTime) {
      carouselSwipeDetectedRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      
      if (deltaX > 0) {
        // Swipe left - next image
        setCurrentImageIndex((prev) => 
          prev === overview.images.length - 1 ? 0 : prev + 1
        );
      } else {
        // Swipe right - previous image
        setCurrentImageIndex((prev) => 
          prev === 0 ? overview.images.length - 1 : prev - 1
        );
      }
    }
  };

  // Swipe handler for full-screen viewer
  const handleFullScreenTouchStart = (e) => {
    fullScreenSwipeDetectedRef.current = false;
    touchStartFullScreenRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now(),
    };
  };

  const handleFullScreenTouchEnd = (e) => {
    if (!overview?.images || overview.images.length <= 1) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    };

    const deltaX = touchStartFullScreenRef.current.x - touchEnd.x;
    const deltaY = touchStartFullScreenRef.current.y - touchEnd.y;
    const deltaTime = Date.now() - touchStartFullScreenRef.current.time;
    const minSwipeDistance = 50;
    const maxSwipeTime = 300; // milliseconds

    // Only handle horizontal swipes (ignore if vertical swipe is larger)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance && deltaTime < maxSwipeTime) {
      fullScreenSwipeDetectedRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      
      if (deltaX > 0) {
        // Swipe left - next image
        setFullScreenImageIndex((prev) => 
          prev === overview.images.length - 1 ? 0 : prev + 1
        );
      } else {
        // Swipe right - previous image
        setFullScreenImageIndex((prev) => 
          prev === 0 ? overview.images.length - 1 : prev - 1
        );
      }
    }
  };

  // Keyboard navigation for full-screen viewer
  useEffect(() => {
    if (!fullScreenViewerOpen || !overview?.images) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setFullScreenImageIndex((prev) => 
          prev === 0 ? overview.images.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight') {
        setFullScreenImageIndex((prev) => 
          prev === overview.images.length - 1 ? 0 : prev + 1
        );
      } else if (e.key === 'Escape') {
        setFullScreenViewerOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullScreenViewerOpen, overview?.images]);

  // Reset in-flight section loading when store changes
  useEffect(() => {
    loadingSectionsRef.current.clear();
  }, [storeId]);

  // Use overview from StoreContext only - do not fetch here to avoid duplicate /overview calls
  useEffect(() => {
    if (contextOverview) {
      setOverview(contextOverview);
      setLoadedSections(prev => {
        const newSet = new Set(prev);
        newSet.add('overview');
        return newSet;
      });
    }
  }, [contextOverview]);

  const loadSectionData = React.useCallback(async (sectionId) => {
    if (loadedSections.has(sectionId)) return;
    if (loadingSectionsRef.current.has(sectionId)) return;
    loadingSectionsRef.current.add(sectionId);

    setLoadingStates(prev => ({ ...prev, [sectionId]: true }));
    
    try {
      switch (sectionId) {
        case 'overview':
          // Already loaded initially
          break;
        case 'location':
          // Location is part of overview, so we can use that data
          // No need to set location separately, we'll use overview.address directly
          setLoadedSections(prev => new Set(prev).add('location'));
          break;
        case 'images':
          setLoadedSections(prev => new Set(prev).add('images'));
          break;
        case 'services':
          try {
            const [servicesData, combos] = await Promise.all([
              StoreService.getServices(true),
              ServiceComboService.getServiceComboCatalog(storeId).catch(() => []),
            ]);
            setServices(mergeCatalogCombos(servicesData, combos));
            setLoadedSections(prev => new Set(prev).add('services'));
          } catch (servicesErr) {
            console.warn('Could not load services:', servicesErr);
            setServices([]);
            setLoadedSections(prev => new Set(prev).add('services'));
          }
          break;
        case 'products':
          if (isPublicFreePlan(overview || contextOverview)) {
            setProducts([]);
            setLoadedSections(prev => new Set(prev).add('products'));
            break;
          }
          try {
            const productsData = await ProductService.getProductCatalog(storeId);
            setProducts(productsData || []);
            setLoadedSections(prev => new Set(prev).add('products'));
          } catch (productsErr) {
            console.warn('Could not load products:', productsErr);
            setProducts([]);
            setLoadedSections(prev => new Set(prev).add('products'));
          }
          break;
        case 'team':
          if (isPublicFreePlan(overview || contextOverview)) {
            setTeamMembers([]);
            setLoadedSections(prev => new Set(prev).add('team'));
            break;
          }
          try {
            const teamMembersResponse = await api.get(`/team-members?storeId=${storeId}&activeOnly=true`);
            setTeamMembers(teamMembersResponse.data || []);
            setLoadedSections(prev => new Set(prev).add('team'));
          } catch (teamErr) {
            console.warn('Could not load team members:', teamErr);
            setTeamMembers([]);
            setLoadedSections(prev => new Set(prev).add('team'));
          }
          break;
        case 'about':
          // TODO: Load about endpoint when available
          // For now, we'll need to add it to overview or create a separate endpoint
          setAbout(null);
          setLoadedSections(prev => new Set(prev).add('about'));
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(`Error loading ${sectionId}:`, err);
    } finally {
      loadingSectionsRef.current.delete(sectionId);
      setLoadingStates(prev => ({ ...prev, [sectionId]: false }));
    }
  }, [storeId, loadedSections, overview, contextOverview]);

  useEffect(() => {
    if (!overview || isPublicFreePlan(overview)) {
      return undefined;
    }

    loadSectionData('products');
    return undefined;
  }, [overview, loadSectionData]);

  // Set up Intersection Observer for lazy loading
  useEffect(() => {
    const sectionIds = ['overview', 'services', 'products', 'team', 'about'];
    
    const observerOptions = {
      root: null,
      rootMargin: '200px', // Start loading 200px before section comes into view
      threshold: 0.1,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.dataset.sectionId;
          const alreadyLoaded = loadedSections.has(sectionId);
          const alreadyLoading = loadingSectionsRef.current.has(sectionId);
          if (!alreadyLoaded && !alreadyLoading) {
            setTimeout(() => loadSectionData(sectionId), 0);
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    // Observe all sections
    sectionIds.forEach((sectionId) => {
      const element = sectionsRef.current[sectionId];
      if (element) {
        element.dataset.sectionId = sectionId;
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [loadedSections, loadSectionData]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    const sectionIds = ['overview', 'services', 'products', 'team', 'about'];
    const sectionId = sectionIds[newValue];
    const sectionElement = sectionsRef.current[sectionId];
    
    if (sectionElement) {
      const offset = 100;
      const elementPosition = sectionElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Scroll detection to show/hide tabs and update active tab
  useEffect(() => {
    const handleScroll = () => {
      const servicesSection = sectionsRef.current.services;
      
      if (servicesSection) {
        const servicesTop = servicesSection.getBoundingClientRect().top;
        // Show tabs when Services section is scrolled past (with some offset)
        const shouldShowTabs = servicesTop < 150;
        setShowTabs(shouldShowTabs);

        // Update active tab based on scroll position (only when tabs are visible)
        if (shouldShowTabs) {
          const sectionIds = ['overview', 'services', 'products', 'team', 'about'];
          const scrollPosition = window.scrollY + 200; // Offset for better detection

          for (let i = sectionIds.length - 1; i >= 0; i--) {
            const sectionId = sectionIds[i];
            const sectionElement = sectionsRef.current[sectionId];
            if (sectionElement) {
              const sectionTop = sectionElement.offsetTop;
              if (scrollPosition >= sectionTop) {
                setSelectedTab(i);
                break;
              }
            }
          }
        } else {
          // When tabs are hidden, we're in Overview section
          setSelectedTab(0);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [overview]);

  const getLocalizedText = (dict, fallback = '') => {
    const storeLanguage = normalizeLanguage(overview?.storeLanguage || contextOverview?.storeLanguage) || DEFAULT_LANGUAGE;
    return getLocalizedValue(dict, currentLanguage, storeLanguage, fallback);
  };

  const formatTime = (timeOnly) => {
    if (!timeOnly) return '';
    const timeStr = timeOnly.toString();
    return timeStr.length === 5 ? timeStr : timeStr.substring(0, 5);
  };

  const formatDuration = (timeOnly) => {
    if (!timeOnly) return '';
    const timeStr = timeOnly.toString();
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (hours === 0) {
      return `${minutes} ${t('common.minutes') || 'min'}`;
    } else if (minutes === 0) {
      return `${hours} ${t('common.hours') || 'hr'}`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  const formatAddress = (address) => {
    if (!address) return '';
    const parts = [
      address.street,
      address.door,
      address.additionalInfo,
      address.city,
      address.state
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleBookNowClick = () => {
    navigate(buildCustomerRoute(storeId, 'booking'), {
      state: {
        services,
        teamMembers,
      },
    });
  };

  const openServiceBooking = (serviceId) => {
    const service = (services || []).find((item) => item.id === serviceId);
    if (service && !hasCustomerBookingDuration(service)) {
      openChat(t('customer.appointments.booking.serviceInterestDraft', {
        serviceName: getLocalizedText(service.name),
      }));
      return;
    }
    navigate(buildCustomerRoute(storeId, 'booking'), {
      state: { services, teamMembers, preSelectedServiceIds: [serviceId] },
    });
  };


  const getOpeningHoursForDay = (weekDayName, openingHours) => {
    if (!openingHours) return null;
    return openingHours.find(oh => oh.weekDay === weekDayName);
  };

  const hasDayPeriods = (hours) => Boolean(hours?.periods?.length);

  const parsePeriodMinutes = (period) => {
    const fromTime = period.from.split(':').map(Number);
    const toTime = period.to.split(':').map(Number);
    return {
      fromMinutes: fromTime[0] * 60 + fromTime[1],
      toMinutes: toTime[0] * 60 + toTime[1],
      from: period.from,
      to: period.to,
    };
  };

  const findContainingPeriod = (periods, currentMinutes) => {
    for (const period of periods || []) {
      const parsed = parsePeriodMinutes(period);
      if (currentMinutes >= parsed.fromMinutes && currentMinutes < parsed.toMinutes) {
        return parsed;
      }
    }
    return null;
  };

  const findUpcomingPeriod = (periods, currentMinutes) => {
    let next = null;
    for (const period of periods || []) {
      const parsed = parsePeriodMinutes(period);
      if (currentMinutes < parsed.fromMinutes && (!next || parsed.fromMinutes < next.fromMinutes)) {
        next = parsed;
      }
    }
    return next;
  };

  const weekDayNames = {
    'Sunday': t('common.days.sunday') || 'Sunday',
    'Monday': t('common.days.monday') || 'Monday',
    'Tuesday': t('common.days.tuesday') || 'Tuesday',
    'Wednesday': t('common.days.wednesday') || 'Wednesday',
    'Thursday': t('common.days.thursday') || 'Thursday',
    'Friday': t('common.days.friday') || 'Friday',
    'Saturday': t('common.days.saturday') || 'Saturday',
  };

  const weekDayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const findNextOpenDay = (openingHours, authorizationHours, currentDay) => {
    if (!openingHours && !authorizationHours) return null;

    const dayIndex = weekDayOrder.indexOf(currentDay);
    if (dayIndex === -1) return null;

    for (let i = 1; i < 7; i++) {
      const nextDayIndex = (dayIndex + i) % 7;
      const nextDay = weekDayOrder[nextDayIndex];
      const nextDayHours = getOpeningHoursForDay(nextDay, openingHours);
      const nextExtraHours = getOpeningHoursForDay(nextDay, authorizationHours);

      if (hasDayPeriods(nextDayHours) || hasDayPeriods(nextExtraHours)) {
        return {
          day: nextDay,
          isTomorrow: i === 1,
          hours: hasDayPeriods(nextDayHours) ? nextDayHours : nextExtraHours,
          isAdditionalHours: !hasDayPeriods(nextDayHours) && hasDayPeriods(nextExtraHours),
        };
      }
    }

    return null;
  };

  const formatNextOpenMessage = (nextOpenDay) => {
    const nextOpenTime = formatTime(nextOpenDay.hours.periods[0].from);
    if (nextOpenDay.isAdditionalHours) {
      const when = nextOpenDay.isTomorrow
        ? (t('customer.store.opensTomorrow') || 'Opens tomorrow')
        : `${t('customer.store.opensOn') || 'Opens on'} ${weekDayNames[nextOpenDay.day]}`;
      return `${t('customer.store.additionalHours') || 'Additional hours'}: ${when} ${t('customer.store.at') || 'at'} ${nextOpenTime}`;
    }

    return nextOpenDay.isTomorrow
      ? `${t('customer.store.opensTomorrow') || 'Opens tomorrow'} ${t('customer.store.at') || 'at'} ${nextOpenTime}`
      : `${t('customer.store.opensOn') || 'Opens on'} ${weekDayNames[nextOpenDay.day]} ${t('customer.store.at') || 'at'} ${nextOpenTime}`;
  };

  const getCurrentOpeningHoursStatus = (openingHours, authorizationHours, specialOpeningDays) => {
    if (!openingHours && !authorizationHours && !(specialOpeningDays || []).length) {
      return { isOpen: false, message: t('customer.store.closed') || 'Closed' };
    }

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentHours = getOpeningHoursForDay(currentDay, openingHours);
    const extraHours = getOpeningHoursForDay(currentDay, authorizationHours);
    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const specialPeriods = (specialOpeningDays || [])
      .filter((item) => String(item.date || '').slice(0, 10) === todayKey)
      .map((item) => ({ from: item.from, to: item.to }))
      .filter((period) => period.from && period.to);
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const openPeriod = findContainingPeriod(
      [...(currentHours?.periods || []), ...specialPeriods],
      currentTime,
    );
    if (openPeriod) {
      return {
        isOpen: true,
        message: `${t('customer.store.openUntil') || 'Open until'} ${formatTime(openPeriod.to)}`
      };
    }

    const extraPeriod = findContainingPeriod(extraHours?.periods, currentTime);
    if (extraPeriod) {
      return {
        isOpen: false,
        message: `${t('customer.store.additionalHoursUntil') || 'Additional hours until'} ${formatTime(extraPeriod.to)}`
      };
    }

    const nextOpenToday = findUpcomingPeriod(
      [...(currentHours?.periods || []), ...specialPeriods],
      currentTime,
    );
    if (nextOpenToday) {
      return {
        isOpen: false,
        message: `${t('customer.store.opensAt') || 'Opens at'} ${formatTime(nextOpenToday.from)}`
      };
    }

    const nextExtraToday = findUpcomingPeriod(extraHours?.periods, currentTime);
    if (nextExtraToday) {
      return {
        isOpen: false,
        message: `${t('customer.store.additionalHoursFrom') || 'Additional hours from'} ${formatTime(nextExtraToday.from)}`
      };
    }

    const nextOpenDay = findNextOpenDay(openingHours, authorizationHours, currentDay);
    if (nextOpenDay) {
      return { isOpen: false, message: formatNextOpenMessage(nextOpenDay) };
    }

    return { isOpen: false, message: t('customer.store.closed') || 'Closed' };
  };

  const overviewLoading = !overview && contextOverviewLoading;
  const isFreePlan = isPublicFreePlan(overview);
  const authorizationHoursForPlan = useMemo(
    () => (isFreePlan ? [] : (overview?.authorizationHours || [])),
    [isFreePlan, overview?.authorizationHours],
  );

  if (overviewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: BRAND.accent }} />
      </Box>
    );
  }

  if (!overview) {
    return null;
  }

  const openingHoursStatus = getCurrentOpeningHoursStatus(
    overview?.openingHours,
    authorizationHoursForPlan,
    overview?.specialOpeningDays,
  );
  const hasProducts = Array.isArray(products) && products.length > 0;

  const tabs = [
    { label: t('customer.store.tabs.overview') || 'Overview', id: 'overview' },
    { label: t('customer.store.tabs.services') || 'Services', id: 'services' },
    ...(!isFreePlan && hasProducts ? [{ label: t('customer.products.title'), id: 'products' }] : []),
    ...(!isFreePlan ? [{ label: t('customer.store.tabs.team') || 'Team', id: 'team' }] : []),
    { label: t('customer.store.tabs.about') || 'About', id: 'about' },
  ];

  return (
    <Box>
      {showTabs && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3,
            position: 'sticky',
            top: 0,
            zIndex: 100,
            bgcolor: 'background.paper',
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: `1px solid ${brandAlpha('accent', 0.2)}`,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
                minHeight: 64,
                color: BRAND.textMuted,
                '&.Mui-selected': {
                  color: BRAND.accentText,
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: BRAND.accent,
                height: 3,
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab key={tab.id} label={tab.label} />
            ))}
          </Tabs>
        </Paper>
      )}

      <Box sx={{ mt: 4 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleBookNowClick}
          startIcon={<EventAvailableIcon sx={{ fontSize: 20 }} />}
          sx={{
            mb: 3,
            py: 1.1,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: { xs: '1rem', sm: '1.05rem' },
            bgcolor: BRAND.accent,
            color: BRAND.onAccent,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: BRAND.accentDark,
              boxShadow: 'none',
            },
          }}
        >
          {t('customer.appointments.bookNow')}
        </Button>

        {/* Overview Section */}
        <Box
          ref={(el) => (sectionsRef.current.overview = el)}
          sx={{ mb: 6, scrollMarginTop: '100px' }}
        >
          <Box
            sx={{
              display: { xs: 'block', lg: 'flex' },
              gap: { lg: 4 },
              flexDirection: { xs: 'column', lg: 'row' },
            }}
          >
            {/* Images Carousel - Left column on large screens */}
            <Box
              sx={{
                width: { xs: '100%', lg: 'calc(50% - 16px)' },
                flexShrink: 0,
                minWidth: 0,
                mb: { xs: 4, md: 4, lg: 0 }, // Add bottom margin when stacked
              }}
            >
              {overview.images && overview.images.length > 0 ? (
                <Box sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                  <Box
                    onClick={() => {
                      if (!carouselSwipeDetectedRef.current) {
                        setFullScreenImageIndex(currentImageIndex);
                        setFullScreenViewerOpen(true);
                      }
                      carouselSwipeDetectedRef.current = false;
                    }}
                    onTouchStart={handleCarouselTouchStart}
                    onTouchEnd={handleCarouselTouchEnd}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '1.7 / 1', // Fixed aspect ratio 1.7:1
                      overflow: 'hidden',
                      cursor: 'pointer',
                      touchAction: 'pan-x pan-y',
                    }}
                  >
                {overview.images.map((image, index) => (
                  <Box
                    key={image.id}
                    sx={{
                      display: index === currentImageIndex ? 'block' : 'none',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={image.url}
                      alt={`${overview.name} - Image ${index + 1}`}
                      sx={{
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Box>
                ))}
                
                {overview.images.length > 1 && (
                  <>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => 
                          prev === 0 ? overview.images.length - 1 : prev - 1
                        );
                      }}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: alpha('#000', 0.5),
                        color: '#fff',
                        zIndex: 2,
                        '&:hover': {
                          bgcolor: alpha('#000', 0.7),
                        },
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => 
                          prev === overview.images.length - 1 ? 0 : prev + 1
                        );
                      }}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: alpha('#000', 0.5),
                        color: '#fff',
                        zIndex: 2,
                        '&:hover': {
                          bgcolor: alpha('#000', 0.7),
                        },
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                    
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 1,
                        zIndex: 2,
                      }}
                    >
                      {overview.images.map((_, index) => (
                        <Box
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: index === currentImageIndex ? '#fff' : alpha('#fff', 0.5),
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            '&:hover': {
                              bgcolor: index === currentImageIndex ? '#fff' : alpha('#fff', 0.8),
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </>
                )}
                </Box>
              </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8, bgcolor: brandAlpha('accent', 0.05), borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('customer.store.noImages') || 'No images available'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Store Info - Right column on large screens */}
            <Box
              sx={{
                width: { xs: '100%', lg: 'calc(50% - 16px)' },
                flexShrink: 0,
                minWidth: 0, // Prevent flex item overflow
                mt: { xs: 4, md: 4, lg: 0 }, // Add top margin on small/medium screens when stacked
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', boxSizing: 'border-box' }}>
                {/* Location */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${brandAlpha('accent', 0.2)}`,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: brandAlpha('accent', 0.3),
                    },
                  }}
                >
                  {/* Icon and Title in same row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: brandAlpha('accent', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <LocationIcon sx={{ color: BRAND.accentText, fontSize: 24 }} />
                    </Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: BRAND.text,
                        fontSize: '1rem',
                      }}
                    >
                      {t('customer.store.location') || 'Location'}
                    </Typography>
                  </Box>
                  {/* Content - Full width */}
                  {overviewLoading ? (
                    <CircularProgress size={20} sx={{ color: BRAND.accent }} />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography 
                        variant="body1" 
                        component="a"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatAddress(overview.address))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          color: BRAND.accentText,
                          lineHeight: 1.6,
                          fontSize: '0.95rem',
                          textDecoration: 'underline',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            color: BRAND.accentDark,
                            textDecorationThickness: '2px',
                          },
                        }}
                      >
                        {formatAddress(overview.address)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Opening Hours */}
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${brandAlpha('accent', 0.2)}`,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      borderColor: brandAlpha('accent', 0.3),
                    },
                  }}
                >
                  {/* Icon and Title in same row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: brandAlpha('accent', 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <TimeIcon sx={{ color: BRAND.accentText, fontSize: 24 }} />
                    </Box>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700, 
                        color: BRAND.text,
                        fontSize: '1rem',
                      }}
                    >
                      {t('customer.store.openingHours') || 'Opening Hours'}
                    </Typography>
                  </Box>
                  {/* Content - Full width */}
                  {overviewLoading ? (
                    <CircularProgress size={20} sx={{ color: BRAND.accent }} />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {/* Current Day Status - Always Visible */}
                      <Box
                        onClick={() => setOpeningHoursExpanded(!openingHoursExpanded)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: 48,
                          px: 1.5,
                          borderRadius: 1.5,
                          bgcolor: brandAlpha('accent', 0.08),
                          border: `1px solid ${brandAlpha('accent', 0.2)}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: brandAlpha('accent', 0.12),
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: openingHoursStatus.isOpen ? BRAND.accentText : BRAND.textMuted,
                              fontSize: '0.9rem',
                            }}
                          >
                            {openingHoursStatus.message}
                          </Typography>
                        </Box>
                        {openingHoursExpanded ? (
                          <ExpandLessIcon sx={{ fontSize: 20, color: BRAND.accentText, flexShrink: 0 }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 20, color: BRAND.accentText, flexShrink: 0 }} />
                        )}
                      </Box>

                      {/* Full Week - Shown when expanded */}
                      {openingHoursExpanded && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.75 }}>
                          {weekDayOrder.map((dayName) => {
                            const hours = getOpeningHoursForDay(dayName, overview?.openingHours);
                            const extraHours = getOpeningHoursForDay(dayName, authorizationHoursForPlan);
                            const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'long' });
                            const isToday = dayName === currentDay;
                            const isOpen = hasDayPeriods(hours);
                            const hasExtraHours = !isFreePlan && hasDayPeriods(extraHours);
                            return (
                              <Box 
                                key={dayName} 
                                sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  py: 0.75,
                                  px: 1.5,
                                  borderRadius: 1.5,
                                  bgcolor: isToday ? brandAlpha('accent', 0.08) : 'transparent',
                                  border: isToday ? `1px solid ${brandAlpha('accent', 0.2)}` : '1px solid transparent',
                                  transition: 'all 0.2s ease',
                                  gap: 1,
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: isToday ? 700 : 500,
                                    color: isToday ? BRAND.accentText : BRAND.text,
                                    fontSize: '0.9rem',
                                  }}
                                >
                                  {weekDayNames[dayName]}
                                </Typography>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: isToday ? 600 : 400,
                                      color: isOpen ? (isToday ? BRAND.accentText : BRAND.textMuted) : BRAND.textSubtle,
                                      fontSize: '0.9rem',
                                    }}
                                  >
                                    {isOpen
                                      ? hours.periods.map(p => `${formatTime(p.from)} - ${formatTime(p.to)}`).join(', ')
                                      : t('customer.store.closed') || 'Closed'}
                                  </Typography>
                                  {hasExtraHours && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        color: BRAND.accentText,
                                        fontWeight: 600,
                                        mt: 0.25,
                                      }}
                                    >
                                      {t('customer.store.additionalHours')}: {extraHours.periods.map(p => `${formatTime(p.from)} - ${formatTime(p.to)}`).join(', ')}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Contact Info */}
                {overview.contacts && overview.contacts.length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${brandAlpha('accent', 0.2)}`,
                      bgcolor: 'background.paper',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        borderColor: brandAlpha('accent', 0.3),
                      },
                    }}
                  >
                    {/* Icon and Title in same row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: brandAlpha('accent', 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <ContactIcon sx={{ color: BRAND.accentText, fontSize: 24 }} />
                      </Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 700, 
                          color: BRAND.text,
                          fontSize: '1rem',
                        }}
                      >
                        {t('customer.store.contact') || 'Contact'}
                      </Typography>
                    </Box>
                    {/* Content - Full width */}
                    {overviewLoading ? (
                      <CircularProgress size={20} sx={{ color: BRAND.accent }} />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {overview.contacts.map((contact, index) => {
                          const getContactIcon = (type) => {
                            if (type === 'Email' || type?.toLowerCase().includes('email')) {
                              return <EmailIcon sx={{ color: BRAND.accentText, fontSize: 20 }} />;
                            }
                            if (type === 'Phone1' || type === 'Phone2' || type?.toLowerCase().includes('phone')) {
                              return <PhoneIcon sx={{ color: BRAND.accentText, fontSize: 20 }} />;
                            }
                            if (type === 'Website' || type?.toLowerCase().includes('website')) {
                              return <WebsiteIcon sx={{ color: BRAND.accentText, fontSize: 20 }} />;
                            }
                            return <PhoneIcon sx={{ color: BRAND.accentText, fontSize: 20 }} />;
                          };

                          const getContactLink = (type, value) => {
                            if (type === 'Email' || type?.toLowerCase().includes('email')) {
                              return `mailto:${value}`;
                            }
                            if (type === 'Phone1' || type === 'Phone2' || type?.toLowerCase().includes('phone')) {
                              return `tel:${value}`;
                            }
                            if (type === 'Website' || type?.toLowerCase().includes('website')) {
                              return value.startsWith('http') ? value : `https://${value}`;
                            }
                            return null;
                          };

                          const isLink = getContactLink(contact.type, contact.value) !== null;
                          const linkProps = isLink ? {
                            component: 'a',
                            href: getContactLink(contact.type, contact.value),
                            ...(contact.type === 'Website' || contact.type?.toLowerCase().includes('website') ? {
                              target: '_blank',
                              rel: 'noopener noreferrer'
                            } : {})
                          } : {};

                          return (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {getContactIcon(contact.type)}
                              </Box>
                              <Typography 
                                variant="body1" 
                                {...linkProps}
                                sx={{ 
                                  color: BRAND.textMuted,
                                  textDecoration: 'none',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  minWidth: 0,
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  ...(isLink ? {
                                    '&:hover': {
                                      color: BRAND.accentText,
                                      textDecoration: 'underline',
                                    },
                                  } : {}),
                                }}
                              >
                                {contact.value}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>



        {/* Services Section */}
        <Box
          ref={(el) => (sectionsRef.current.services = el)}
          sx={{ mb: 6, scrollMarginTop: '100px' }}
        >
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, color: BRAND.accentText }}>
            {t('customer.store.tabs.services') || 'Services'}
          </Typography>
          
          {loadingStates.services ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: BRAND.accent }} />
            </Box>
          ) : services && services.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {services
                  .filter((service) => isCustomerBookableService(service) && !service.isCombo)
                  .slice(0, showAllServices ? services.length : 5)
                  .map((service, index) => {
                    const serviceName = getLocalizedText(service.name);
                    const serviceDescription = getLocalizedText(service.description);
                    const visibleServices = services.filter((service) => isCustomerBookableService(service) && !service.isCombo);
                    const isLast = index === (showAllServices ? visibleServices.length : Math.min(5, visibleServices.length)) - 1;
                    return (
                      <Box
                        key={service.id}
                        role={canClickServiceRows ? 'button' : undefined}
                        tabIndex={canClickServiceRows ? 0 : undefined}
                        onClick={canClickServiceRows ? () => openServiceBooking(service.id) : undefined}
                        onKeyDown={canClickServiceRows
                          ? (event) => {
                            if (event.key !== 'Enter' && event.key !== ' ') return;
                            event.preventDefault();
                            openServiceBooking(service.id);
                          }
                          : undefined}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1.5, sm: 2 },
                          p: 2.5,
                          borderBottom: isLast ? 'none' : `1px solid ${brandAlpha('accent', 0.2)}`,
                          cursor: canClickServiceRows ? 'pointer' : 'default',
                          transition: 'background-color 0.2s ease',
                          ...(canClickServiceRows ? {
                            '&:hover': {
                              bgcolor: brandAlpha('accent', 0.08),
                            },
                          } : {}),
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              mb: 0.75, 
                              fontWeight: 600,
                              color: BRAND.text,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                            title={serviceName}
                          >
                            {serviceName}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mb: { xs: 1, sm: 0 },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                            title={serviceDescription}
                          >
                            {serviceDescription}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'row', sm: 'column' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'center', sm: 'flex-end' },
                          gap: { xs: 2, sm: 1 },
                          flexShrink: 0,
                        }}>
                          <Chip
                            label={formatServiceDurationLabel(service, { onRequestLabel: t('servicesManagement.valueMode.onRequest') })}
                            size="small"
                            sx={{ bgcolor: brandAlpha('accent', 0.1), color: BRAND.accentText }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.accentText, whiteSpace: 'nowrap' }}>
                            {formatServicePriceLabel(service, {
                              formatMoney: (amount) => formatCurrency(amount, overview.currency),
                              onRequestLabel: t('servicesManagement.valueMode.onRequest'),
                              freeLabel: t('customer.store.freePrice'),
                              fromLabel: (price) => t('servicesManagement.valueMode.priceFrom', { price }),
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
              {services.filter((service) => isCustomerBookableService(service) && !service.isCombo).length > 5 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    onClick={() => setShowAllServices(!showAllServices)}
                    endIcon={showAllServices ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      color: BRAND.accentText,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: brandAlpha('accent', 0.1),
                      },
                    }}
                  >
                    {showAllServices 
                      ? (t('common.seeLess') || 'See Less')
                      : (t('common.seeMore') || 'See More')
                    }
                  </Button>
                </Box>
              )}
            </>
            ) : (
              <Alert severity="info">
                {t('customer.store.noServices') || 'No services available'}
              </Alert>
            )}
        </Box>

        {Array.isArray(services) && services.some((service) => isCustomerBookableService(service) && service.isCombo) ? (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, color: BRAND.accentText }}>
              {t('customer.appointments.booking.categoryCombos')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {services
                .filter((service) => isCustomerBookableService(service) && service.isCombo)
                .map((service, index, comboServices) => {
                  const serviceName = getLocalizedText(service.name);
                  const serviceDescription = getLocalizedText(service.description);
                  return (
                    <Box
                      key={service.id}
                      role={canClickServiceRows ? 'button' : undefined}
                      tabIndex={canClickServiceRows ? 0 : undefined}
                      onClick={canClickServiceRows ? () => openServiceBooking(service.id) : undefined}
                      onKeyDown={canClickServiceRows
                        ? (event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          openServiceBooking(service.id);
                        }
                        : undefined}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 1.5, sm: 2 },
                        p: 2.5,
                        borderBottom: index === comboServices.length - 1 ? 'none' : `1px solid ${brandAlpha('accent', 0.2)}`,
                        cursor: canClickServiceRows ? 'pointer' : 'default',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: BRAND.text }}>
                          {serviceName}
                        </Typography>
                        {serviceDescription ? (
                          <Typography variant="body2" color="text.secondary">
                            {serviceDescription}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                        <Chip
                          label={formatDuration(service.duration)}
                          size="small"
                          sx={{ bgcolor: brandAlpha('accent', 0.1), color: BRAND.accentText }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.accentText, whiteSpace: 'nowrap' }}>
                          {Number(service.price) === 0
                            ? t('customer.store.freePrice')
                            : `${getCurrencySymbol(overview.currency)}${parseFloat(service.price).toFixed(2)}`}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        ) : null}

        {!isFreePlan && hasProducts && (
        <Box
          ref={(el) => (sectionsRef.current.products = el)}
          sx={{ mb: 6, scrollMarginTop: '100px' }}
        >
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" component="h2" sx={{ mb: 1.5, fontWeight: 600, color: BRAND.accentText }}>
            {t('customer.products.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('customer.products.subtitle')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {products
              .slice(0, showAllProducts ? products.length : 5)
              .map((product, index) => {
                const productName = getLocalizedText(product.name);
                const productDescription = getLocalizedText(product.description);
                const isLast = index === (showAllProducts ? products.length : Math.min(5, products.length)) - 1;
                return (
                  <Box
                    key={product.id}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1.5, sm: 2 },
                      p: 2.5,
                      borderBottom: isLast ? 'none' : `1px solid ${brandAlpha('accent', 0.2)}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {productName}
                      </Typography>
                      {productDescription ? (
                        <Typography variant="body2" color="text.secondary">
                          {productDescription}
                        </Typography>
                      ) : null}
                      {product.isOutOfStock ? (
                        <Chip size="small" label={t('customer.products.outOfStock')} sx={{ mt: 1 }} />
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: BRAND.accentText }}>
                        {formatCurrency(product.price, overview?.currency || contextOverview?.currency)}
                      </Typography>
                      <Button
                        onClick={() => navigate(buildCustomerRoute(storeId, 'booking'), {
                          state: {
                            services,
                            teamMembers,
                            preSelectedProductIds: [product.id],
                          },
                        })}
                        disabled={product.isOutOfStock}
                        sx={{ textTransform: 'none', color: BRAND.accentText, fontWeight: 600 }}
                      >
                        {t('customer.products.addWhenBooking')}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
          </Box>
          {products.length > 5 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                onClick={() => setShowAllProducts(!showAllProducts)}
                endIcon={showAllProducts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: BRAND.accentText, fontWeight: 600, textTransform: 'none' }}
              >
                {showAllProducts ? (t('common.seeLess') || 'See Less') : (t('common.seeMore') || 'See More')}
              </Button>
            </Box>
          ) : null}
        </Box>
        )}

        {/* Team Members Section */}
        {!isFreePlan && (
        <Box
          ref={(el) => (sectionsRef.current.team = el)}
          sx={{ mb: 6, scrollMarginTop: '100px' }}
        >
          <Divider sx={{ my: 4 }} />
          <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600, color: BRAND.accentText }}>
            {t('customer.store.tabs.team') || 'Team'}
          </Typography>
          
          {loadingStates.team ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: BRAND.accent }} />
            </Box>
          ) : teamMembers && teamMembers.length > 0 ? (
            <Grid container spacing={3} sx={{ pt: 1 }}>
              {teamMembers
                .filter(member => !member.disabled && member.acceptsCustomerBookings !== false)
                .map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${brandAlpha('accent', 0.2)}`,
                        textAlign: 'center',
                        transition: 'all 0.3s',
                        '&:hover': {
                          border: `1px solid ${brandAlpha('accent', 0.4)}`,
                          boxShadow: 'none',
                          transform: 'translateY(-4px)',
                        }
                      }}
                    >
                      <CardContent>
                        <Avatar
                          src={member.picture ?? member.Picture}
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 2,
                            bgcolor: BRAND.accent,
                            color: BRAND.onAccent,
                            fontSize: '2rem',
                            fontWeight: 700,
                          }}
                        >
                          {member.name?.charAt(0)?.toUpperCase() || 'T'}
                        </Avatar>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          {member.name}
                        </Typography>
                        {member.jobTitle && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {member.jobTitle}
                          </Typography>
                        )}
                        {member.services && member.services.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', minWidth: 0 }}>
                            {member.services.slice(0, 3).map((service) => {
                              const serviceName = getLocalizedText(service.name);
                              const displayLabel = serviceName.length > 30 ? `${serviceName.slice(0, 27)}...` : serviceName;
                              return (
                                <Chip
                                  key={service.id}
                                  label={displayLabel}
                                  title={serviceName}
                                  size="small"
                                  sx={{
                                    bgcolor: brandAlpha('accent', 0.1),
                                    color: BRAND.accentText,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              );
                            })}
                            {member.services.length > 3 && (
                              <Chip
                                label={`+${member.services.length - 3}`}
                                size="small"
                                sx={{
                                  bgcolor: brandAlpha('accent', 0.1),
                                  color: BRAND.accentText,
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          ) : (
            <Alert severity="info">
              {t('customer.store.noTeamMembers') || 'No team members available'}
            </Alert>
          )}
        </Box>
        )}

        {/* About Section */}
        {about !== null && (
          <Box
            ref={(el) => (sectionsRef.current.about = el)}
            sx={{ mb: 6, scrollMarginTop: '100px' }}
          >
            <Divider sx={{ my: 4 }} />
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: BRAND.text }}>
              {t('customer.store.tabs.about') || 'About'}
            </Typography>
            
            {loadingStates.about ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: BRAND.accent }} />
              </Box>
            ) : about && Object.keys(about).length > 0 ? (
              <Card elevation={0} sx={{ bgcolor: brandAlpha('accent', 0.05), borderRadius: 2, p: 3 }}>
                <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                  {getLocalizedText(about)}
                </Typography>
              </Card>
            ) : (
              <Alert severity="info">
                {t('customer.store.noAbout') || 'No information available'}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {/* Full Screen Image Viewer */}
      {overview?.images && overview.images.length > 0 && (
        <Dialog
          open={fullScreenViewerOpen}
          onClose={() => setFullScreenViewerOpen(false)}
          maxWidth={false}
          disableScrollLock={true}
          PaperProps={{
            sx: {
              width: { xs: '100vw', sm: '90vw' },
              height: { xs: '100vh', sm: '90vh' },
              maxWidth: { xs: '100vw', sm: '90vw' },
              maxHeight: { xs: '100vh', sm: '90vh' },
              margin: 0,
              borderRadius: { xs: 0, sm: 2 },
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              overflow: 'hidden',
            }
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={() => setFullScreenViewerOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
                bgcolor: alpha('#000', 0.5),
                color: '#fff',
                '&:hover': {
                  bgcolor: alpha('#000', 0.7),
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Image Container */}
            <Box
              onTouchStart={handleFullScreenTouchStart}
              onTouchEnd={handleFullScreenTouchEnd}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                p: { xs: 2, sm: 4 },
                pb: overview.images.length > 1 ? { xs: 6, sm: 8 } : { xs: 2, sm: 4 },
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                touchAction: 'pan-x pan-y',
              }}
            >
              {overview.images.map((image, index) => (
                <Box
                  key={image.id}
                  sx={{
                    display: index === fullScreenImageIndex ? 'flex' : 'none',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={image.url}
                    alt={`${overview.name} - Image ${index + 1}`}
                    sx={{
                      maxWidth: { xs: 'calc(100vw - 32px)', sm: 'calc(90vw - 64px)' },
                      maxHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(90vh - 120px)' },
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
              ))}

              {/* Navigation Arrows */}
              {overview.images.length > 1 && (
                <>
                  <IconButton
                    onClick={() => setFullScreenImageIndex((prev) => 
                      prev === 0 ? overview.images.length - 1 : prev - 1
                    )}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: alpha('#000', 0.5),
                      color: '#fff',
                      '&:hover': {
                        bgcolor: alpha('#000', 0.7),
                      },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setFullScreenImageIndex((prev) => 
                      prev === overview.images.length - 1 ? 0 : prev + 1
                    )}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: alpha('#000', 0.5),
                      color: '#fff',
                      '&:hover': {
                        bgcolor: alpha('#000', 0.7),
                      },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Image Counter and Dots */}
            {overview.images.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#fff',
                    bgcolor: alpha('#000', 0.5),
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {fullScreenImageIndex + 1} / {overview.images.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {overview.images.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setFullScreenImageIndex(index)}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: index === fullScreenImageIndex ? '#fff' : alpha('#fff', 0.5),
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          bgcolor: index === fullScreenImageIndex ? '#fff' : alpha('#fff', 0.8),
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default StoreDetailPage;

